import React from 'react';
import { Box, Button, IconButton, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useTranslation } from 'react-i18next';
import StorePaymentMethodField from './StorePaymentMethodField';
import { buildTextFieldSx, fieldLabelSx } from './formFieldStyles';
import { IN_STORE_PAYMENT_METHODS } from '../../constants/StorePaymentMethod';
import { formatCurrency, DEFAULT_CURRENCY } from '../../utils/currencyUtils';
import {
  createSplitPaymentLine,
  sumSplitPaymentLines,
  toSplitAmount,
} from '../../utils/splitPaymentUtils';
import { BRAND } from '../../theme/brandTokens';

const SplitPaymentFields = ({
  lines,
  onChange,
  amountDue = 0,
  currencyCode = DEFAULT_CURRENCY,
  allowDiscount = true,
  disabled = false,
  errors = {},
}) => {
  const { t } = useTranslation();
  const due = Math.max(0, toSplitAmount(amountDue));
  const collected = sumSplitPaymentLines(lines);
  const remaining = Math.max(0, Math.round((due - collected) * 100) / 100);
  const usedMethods = (lines || []).map((line) => line.paymentMethod).filter(Boolean);
  const canAddLine = !disabled
    && usedMethods.length < IN_STORE_PAYMENT_METHODS.length
    && (lines || []).length < IN_STORE_PAYMENT_METHODS.length;

  const updateLine = (lineId, patch) => {
    onChange((lines || []).map((line) => (
      line.id === lineId ? { ...line, ...patch } : line
    )));
  };

  const removeLine = (lineId) => {
    if ((lines || []).length <= 1) {
      return;
    }

    onChange(lines.filter((line) => line.id !== lineId));
  };

  const addLine = () => {
    onChange([...(lines || []), createSplitPaymentLine(remaining > 0 ? remaining.toFixed(2) : '')]);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {(lines || []).map((line, index) => {
        const methodError = errors[`line-${index}-method`];
        const amountError = errors[`line-${index}-amount`];
        const excludeMethods = usedMethods.filter((method) => method !== line.paymentMethod);

        return (
          <Box
            key={line.id}
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2,
              alignItems: { xs: 'stretch', sm: 'flex-start' },
            }}
          >
            <Box sx={{ flex: 1 }}>
              <StorePaymentMethodField
                label={t('appointmentCheckout.paymentLine', { number: index + 1 })}
                value={line.paymentMethod}
                onChange={(event) => updateLine(line.id, { paymentMethod: event.target.value })}
                disabled={disabled}
                error={Boolean(methodError)}
                helperText={methodError ? t(methodError) : ''}
                excludeMethods={excludeMethods}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" sx={fieldLabelSx}>
                {t('appointmentCheckout.amountReceived')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <TextField
                  fullWidth
                  type="number"
                  value={line.amount}
                  onChange={(event) => updateLine(line.id, { amount: event.target.value })}
                  disabled={disabled}
                  error={Boolean(amountError)}
                  helperText={amountError ? t(amountError) : ''}
                  inputProps={{ min: 0, step: '0.01' }}
                  sx={buildTextFieldSx(Boolean(amountError))}
                />
                {(lines || []).length > 1 ? (
                  <IconButton
                    aria-label={t('appointmentCheckout.removePayment')}
                    onClick={() => removeLine(line.id)}
                    disabled={disabled}
                    sx={{ mt: 0.5, color: BRAND.accentText }}
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                ) : null}
              </Box>
            </Box>
          </Box>
        );
      })}

      <Box>
        <Button
          onClick={addLine}
          disabled={!canAddLine}
          startIcon={<AddIcon />}
          color="inherit"
          sx={{ color: BRAND.accentText, fontWeight: 600, px: 0 }}
        >
          {t('appointmentCheckout.addPayment')}
        </Button>
      </Box>

      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: BRAND.primaryText }}>
        {t('appointmentCheckout.totalCollected', { amount: formatCurrency(collected, currencyCode) })}
      </Typography>
      {remaining > 0 ? (
        <Typography variant="body2" color="text.secondary">
          {allowDiscount
            ? t('appointmentCheckout.discount', { amount: formatCurrency(remaining, currencyCode) })
            : t('appointmentCheckout.remaining', { amount: formatCurrency(remaining, currencyCode) })}
        </Typography>
      ) : null}
      {errors.total ? (
        <Typography variant="body2" sx={{ color: '#f44336' }}>
          {t(errors.total)}
        </Typography>
      ) : null}
    </Box>
  );
};

export default SplitPaymentFields;
